import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { ListResponse } from "@app/api/client";
import { api } from "@app/api/client";
import type { BackofficeOrganization, BackofficeOrganizationDetail } from "@app/api/types";

export interface OrganizationListParams {
  offset: number;
  limit: number;
  q?: string;
}

export function useOrganizations(params: OrganizationListParams) {
  return useQuery({
    queryKey: ["organizations", params],
    queryFn: async (): Promise<ListResponse<BackofficeOrganization>> => {
      const query: Record<string, string | number> = { offset: params.offset, limit: params.limit };
      if (params.q) query.q = params.q;
      const { data } = await api.get<ListResponse<BackofficeOrganization>>("/admin/organizations", {
        params: query
      });
      return data;
    }
  });
}

export function useOrganization(id: number) {
  return useQuery({
    queryKey: ["organization", id],
    queryFn: async (): Promise<BackofficeOrganizationDetail> => {
      const { data } = await api.get<BackofficeOrganizationDetail>(`/admin/organizations/${id}`);
      return data;
    }
  });
}

export function useSetOrganizationDisabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, disabled }: { id: number; disabled: boolean }): Promise<BackofficeOrganization> => {
      const { data } = await api.post<BackofficeOrganization>(
        `/admin/organizations/${id}/${disabled ? "disable" : "enable"}`
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["organization", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    }
  });
}
